import { ApiError, unwrap, type Schemas } from '@mrigl/api-client';
import { Alert, Button, Card, TextField } from '@mrigl/ui';
import { useCallback, useEffect, useState, type SubmitEvent } from 'react';
import { Link, useParams } from 'react-router';
import { api, errorMessage } from '../../api.js';
import { Loading } from '../../components.js';
import { useI18n, type I18n } from '../../i18n.js';
import { display, useStore } from '../catalog/shared.js';

type Domains = Schemas['DomainList'];
type Domain = Domains['items'][number];

function domainError(t: I18n['t'], failure: unknown): string {
  if (failure instanceof ApiError) {
    if (failure.code === 'domain-not-verified') return t('admin.domains.error.notVerified');
    if (failure.code === 'validation-failed') return t('admin.domains.error.invalid');
    if (failure.code === 'conflict') return t('admin.domains.error.taken');
  }
  return errorMessage(t, failure);
}

/** The store's addresses: mrigl subdomain and custom domains (DNS verification, main address). */
export function DomainsPage() {
  const { storeId = '' } = useParams();
  const { t, locale } = useI18n();
  const { store, error: storeError } = useStore(storeId, t);
  const [domains, setDomains] = useState<Domains | null>(null);
  const [host, setHost] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setDomains(
      await unwrap(
        api.GET('/admin-api/v1/stores/{storeId}/domains', { params: { path: { storeId } } }),
      ),
    );
  }, [storeId]);

  useEffect(() => {
    load().catch((failure: unknown) => {
      setError(errorMessage(t, failure));
    });
  }, [load, t]);

  async function act(run: () => Promise<Domains>, message: string) {
    setBusy(true);
    setNotice(null);
    setError(null);
    try {
      setDomains(await run());
      setNotice(message);
      return true;
    } catch (failure) {
      setError(domainError(t, failure));
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function add(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    const ok = await act(
      () =>
        unwrap(
          api.POST('/admin-api/v1/stores/{storeId}/domains', {
            params: { path: { storeId } },
            body: { host: host.trim() },
          }),
        ),
      t('admin.domains.added'),
    );
    if (ok) setHost('');
  }

  const path = (domain: Domain) => ({ params: { path: { storeId, hostId: domain.hostId } } });

  if (storeError !== null) return <Alert tone="error">{storeError}</Alert>;
  if (store === null || domains === null) {
    return error === null ? <Loading /> : <Alert tone="error">{error}</Alert>;
  }
  const canManage = store.permissions.includes('store.update');
  return (
    <div className="mrigl-stack">
      <p>
        <Link to={`/stores/${storeId}`}>{display(store.name, locale, store, store.slug)}</Link>
      </p>
      <h1 className="mrigl-heading">{t('admin.domains.title')}</h1>
      <p>{t('admin.domains.intro')}</p>
      <div role="status">{notice === null ? null : <Alert tone="success">{notice}</Alert>}</div>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      {domains.items.map((domain) => (
        <Card key={domain.hostId} labelledBy={`domain-${domain.hostId}`}>
          <h2 id={`domain-${domain.hostId}`} className="mrigl-subheading" dir="ltr">
            {domain.host}
          </h2>
          <p>
            {t(`admin.domains.kind.${domain.kind}`)} — {t(`admin.domains.status.${domain.status}`)}
            {domain.canonical ? ` — ${t('admin.domains.canonical')}` : ''}
          </p>
          {domain.instructions === null ? null : (
            <>
              <p>{t('admin.domains.instructions')}</p>
              <ul className="mrigl-list" dir="ltr">
                <li>
                  <code>
                    {t('admin.domains.txt', {
                      name: domain.instructions.txtName,
                      value: domain.instructions.txtValue,
                    })}
                  </code>
                </li>
                <li>
                  <code>
                    {t('admin.domains.cname', {
                      name: domain.instructions.cnameName,
                      target: domain.instructions.cnameTarget,
                    })}
                  </code>
                </li>
              </ul>
            </>
          )}
          {canManage ? (
            <p className="admin-actions">
              {domain.status === 'pending_verification' ? (
                <Button
                  busy={busy}
                  onClick={() =>
                    void act(
                      () =>
                        unwrap(
                          api.POST(
                            '/admin-api/v1/stores/{storeId}/domains/{hostId}/verify',
                            path(domain),
                          ),
                        ),
                      t('admin.domains.verified'),
                    )
                  }
                >
                  {t('admin.domains.verify', { host: domain.host })}
                </Button>
              ) : null}
              {domain.status === 'active' && !domain.canonical ? (
                <Button
                  variant="secondary"
                  busy={busy}
                  onClick={() =>
                    void act(
                      () =>
                        unwrap(
                          api.POST(
                            '/admin-api/v1/stores/{storeId}/domains/{hostId}/canonical',
                            path(domain),
                          ),
                        ),
                      t('admin.domains.canonicalDone'),
                    )
                  }
                >
                  {t('admin.domains.makeCanonical', { host: domain.host })}
                </Button>
              ) : null}
              {domain.kind === 'custom' ? (
                <Button
                  variant="secondary"
                  busy={busy}
                  onClick={() =>
                    void act(
                      () =>
                        unwrap(
                          api.DELETE(
                            '/admin-api/v1/stores/{storeId}/domains/{hostId}',
                            path(domain),
                          ),
                        ),
                      t('admin.domains.removed'),
                    )
                  }
                >
                  {t('admin.domains.remove', { host: domain.host })}
                </Button>
              ) : null}
            </p>
          ) : null}
        </Card>
      ))}
      {!domains.available ? (
        <p className="mrigl-muted">{t('admin.domains.unavailable')}</p>
      ) : canManage ? (
        <form
          className="mrigl-card mrigl-stack"
          aria-label={t('admin.domains.add')}
          onSubmit={(event) => void add(event)}
        >
          <TextField
            label={t('admin.domains.host')}
            dir="ltr"
            required
            maxLength={253}
            value={host}
            onChange={(event) => {
              setHost(event.target.value);
            }}
          />
          <Button type="submit" busy={busy}>
            {t('admin.domains.add')}
          </Button>
        </form>
      ) : null}
    </div>
  );
}
